import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { cashApi } from '@/store/cash/api/index.ts';
import type { CashById, FxCashNdf } from '@/store/cash/state.ts';

/**
 * Matcher for updating fixing sources in the cash store.
 * @param builder - The reducer builder
 */
export function updateFixingSource(builder: ActionReducerMapBuilder<CashById>) {
  builder.addMatcher(cashApi.endpoints.getFixingReferences.matchFulfilled, (state, action) => {
    const { tileId } = action.meta.arg.originalArgs || {};
    const tile = state[tileId];

    if (isNotDefined(tile) || tile.type !== 'FxNdf') {
      return;
    }

    // Extract fixing references from payload
    const fixingReferences = action.payload[0]?.FixingId;
    const sndFixingReferences = action.payload[1]?.FixingId;

    // Update main and secondary fixing sources using dedicated functions
    updateMainFixingSource(tile, fixingReferences);
    updateSecondaryFixingSource(tile, sndFixingReferences);
  });
}

/**
 * Updates the main fixing source for the given tile.
 * @param tile - The tile to update (FxCashNdf)
 * @param fixingReferences - The main fixing reference value
 */
function updateMainFixingSource(tile: FxCashNdf, fixingReferences: string | undefined): void {
  if (isNotDefined(fixingReferences)) {
    return;
  }

  // Set main fixing source
  tile.properties.fixingSource = fixingReferences;

  // Override default if possible sources are defined
  if (isDefined(tile.properties.possibleFixingSources)) {
    tile.properties.fixingSourceOverriddenDefault = fixingReferences;
  }
}

/**
 * Updates the secondary fixing source for the given tile.
 * @param tile - The tile to update (FxCashNdf)
 * @param sndFixingReferences - The secondary fixing reference value
 */
function updateSecondaryFixingSource(tile: FxCashNdf, sndFixingReferences: string | undefined): void {
  if (isNotDefined(sndFixingReferences)) {
    return;
  }

  // Set secondary fixing source (uses main fixing reference)
  tile.properties.sndFixingSource = sndFixingReferences;

  // Override default if possible sources are defined
  if (isDefined(tile.properties.possibleSndFixingSources)) {
    tile.properties.sndFixingSourceOverriddenDefault = sndFixingReferences;
  }
}
